import { Suspense, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@cbhq/cds-web/buttons/Button';
import { IconButton } from '@cbhq/cds-web/buttons/IconButton';
import { Icon } from '@cbhq/cds-web/icons/Icon';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { Pressable } from '@cbhq/cds-web/system/Pressable';
import { Text } from '@cbhq/cds-web/typography/Text';
import { defineMessages, useIntl } from '@cbhq/i18n';

import { InAppErrorMessageFallback } from '../../../components/ErrorBoundary/InAppErrorMessageFallback';
import { teamAccessMessages } from '../../../components/ErrorBoundary/messages';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useDomainClaimsData } from '../hooks/useDomainClaimsData';
import { ROUTE_SECURITY } from '../utils/constants';

import { AddDomainTray } from './components/AddDomainTray';
import { DomainClaimsSkeleton } from './components/DomainClaimsSkeleton';
import { DomainClaimsTable } from './components/DomainClaimsTable';
import { HowItWorksTray } from './components/HowItWorksTray';

const messages = defineMessages({
  backLink: {
    id: 'Security.claimADomain.backLink',
    defaultMessage: 'Security',
    description: 'Back link label from the Claim a domain page to the Security page',
  },
  backLinkAccessibilityLabel: {
    id: 'Security.claimADomain.backLinkAccessibilityLabel',
    defaultMessage: 'Back to Security',
    description: 'Accessibility label for navigating back to the Security page',
  },
  title: {
    id: 'Security.claimADomain.title',
    defaultMessage: 'Claim a domain',
    description: 'Title for the workspace Claim a domain page',
  },
  description: {
    id: 'Security.claimADomain.description',
    defaultMessage: "Verify your organization's domain",
    description: 'Description shown on the Claim a domain page',
  },
  addDomain: {
    id: 'Security.claimADomain.addDomain',
    defaultMessage: 'Add domain',
    description: 'Button label to add a new claimed domain',
  },
  noOrgSelectedTitle: {
    id: 'Security.claimADomain.noOrgSelectedTitle',
    defaultMessage: 'No organization selected',
    description: 'Title shown when no organization is selected on the Claim a domain page',
  },
  noOrgSelectedDescription: {
    id: 'Security.claimADomain.noOrgSelectedDescription',
    defaultMessage: 'Select an organization to view its claimed domains.',
    description: 'Description shown when no organization is selected on the Claim a domain page',
  },
  emptyState: {
    id: 'Security.claimADomain.emptyState',
    defaultMessage: 'Your organization has not claimed any domains yet.',
    description: 'Message shown when the organization has no claimed domains',
  },
  permissionDeniedTitle: {
    id: 'Security.claimADomain.permissionDeniedTitle',
    defaultMessage: "You don't have access to this organization's domain claims",
    description:
      'Title shown when the user lacks account_management/org-level admin access to view domain claims',
  },
  permissionDeniedDescription: {
    id: 'Security.claimADomain.permissionDeniedDescription',
    defaultMessage:
      'Only organization or account admins can view claimed domains. Contact your organization administrator for access.',
    description:
      'Description shown when the user lacks account_management/org-level admin access to view domain claims',
  },
  learnMore: {
    id: 'Security.claimADomain.learnMore',
    defaultMessage: 'Learn more',
    description: 'Button label to open the how-it-works tray from the empty state',
  },
  howItWorksAccessibilityLabel: {
    id: 'Security.claimADomain.howItWorksAccessibilityLabel',
    defaultMessage: 'How domain claims work',
    description: 'Accessibility label for the info icon that explains how domain claims work',
  },
});

type ClaimADomainContentProps = {
  onLearnMoreClick: () => void;
};

function ClaimADomainContent({ onLearnMoreClick }: ClaimADomainContentProps) {
  const { formatMessage } = useIntl();
  const { domainClaims, isPermissionDenied } = useDomainClaimsData();

  if (isPermissionDenied) {
    return (
      <InAppErrorMessageFallback
        title={formatMessage(messages.permissionDeniedTitle)}
        description={formatMessage(messages.permissionDeniedDescription)}
      />
    );
  }

  if (domainClaims.length === 0) {
    return (
      <VStack gap={3} alignItems="flex-start">
        <Text font="body" color="fgMuted">
          {formatMessage(messages.emptyState)}
        </Text>
        <Button
          variant="secondary"
          compact
          onClick={onLearnMoreClick}
          testID="claim-a-domain-learn-more-button"
        >
          {formatMessage(messages.learnMore)}
        </Button>
      </VStack>
    );
  }

  return <DomainClaimsTable domainClaims={domainClaims} />;
}

export function ClaimADomainPage() {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { selectedOrganization, hasRequestedOrganizationAccessDenied } = useOrganization();
  const organizationId = selectedOrganization?.organizationId ?? '';
  const [isAddDomainTrayOpen, setIsAddDomainTrayOpen] = useState(false);
  const [isHowItWorksTrayOpen, setIsHowItWorksTrayOpen] = useState(false);

  const handleBackClick = useCallback(() => {
    navigate(ROUTE_SECURITY);
  }, [navigate]);

  const handleAddDomainClick = useCallback(() => {
    setIsAddDomainTrayOpen(true);
  }, []);

  const handleAddDomainTrayClose = useCallback(() => {
    setIsAddDomainTrayOpen(false);
  }, []);

  const handleHowItWorksClick = useCallback(() => {
    setIsHowItWorksTrayOpen(true);
  }, []);

  const handleHowItWorksTrayClose = useCallback(() => {
    setIsHowItWorksTrayOpen(false);
  }, []);

  if (hasRequestedOrganizationAccessDenied) {
    return (
      <InAppErrorMessageFallback
        title={formatMessage(teamAccessMessages.deniedTitle)}
        description={formatMessage(teamAccessMessages.deniedDescription)}
      />
    );
  }

  if (!organizationId) {
    return (
      <InAppErrorMessageFallback
        title={formatMessage(messages.noOrgSelectedTitle)}
        description={formatMessage(messages.noOrgSelectedDescription)}
      />
    );
  }

  return (
    <VStack
      className="workspaceContentFadeIn"
      gap={6}
      width="100%"
      data-testid="claim-a-domain-page"
    >
      <VStack gap={4} width="100%">
        <Pressable
          background="transparent"
          noScaleOnPress
          onClick={handleBackClick}
          accessibilityLabel={formatMessage(messages.backLinkAccessibilityLabel)}
        >
          <HStack alignItems="center" gap={2}>
            <Icon name="caretLeft" size="s" color="fgMuted" />
            <Text as="span" font="label1" color="fgMuted">
              {formatMessage(messages.backLink)}
            </Text>
          </HStack>
        </Pressable>
        <HStack alignItems="flex-start" justifyContent="space-between" width="100%" gap={4}>
          <VStack gap={1} width="100%">
            <Text as="h1" font="title1">
              {formatMessage(messages.title)}
            </Text>
            <HStack alignItems="center" gap={1}>
              <Text font="label2" color="fgMuted">
                {formatMessage(messages.description)}
              </Text>
              <IconButton
                transparent
                compact
                color="fgMuted"
                name="info"
                onClick={handleHowItWorksClick}
                accessibilityLabel={formatMessage(messages.howItWorksAccessibilityLabel)}
                testID="claim-a-domain-how-it-works"
              />
            </HStack>
          </VStack>
          <Button
            variant="primary"
            compact
            onClick={handleAddDomainClick}
            testID="add-domain-button"
          >
            {formatMessage(messages.addDomain)}
          </Button>
        </HStack>
      </VStack>

      <Suspense fallback={<DomainClaimsSkeleton />}>
        <ClaimADomainContent key={organizationId} onLearnMoreClick={handleHowItWorksClick} />
      </Suspense>

      {isAddDomainTrayOpen ? <AddDomainTray onClose={handleAddDomainTrayClose} /> : null}
      {isHowItWorksTrayOpen ? <HowItWorksTray onClose={handleHowItWorksTrayClose} /> : null}
    </VStack>
  );
}
